import React, { Fragment } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Field } from 'react-final-form';
import Stack from '@mui/material/Stack';
import ToggleButtonGroup from 'components/ToggleButtonGroup';
import { TextField } from '../../../lib/mui-rff/TextField';
import { FieldArray } from 'react-final-form-arrays';
import debug from 'debug';
import Button from '@mui/material/Button';
import { Accordion, AccordionSummary, AccordionDetails, AccordionWrapper } from 'components/Accordion';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import get from 'lodash/get';
import RecipientForm from './RecipientForm';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { composeValidators, isRequired, isValidEmail, isValidPhoneNumber } from '../../../utilities/regEx';
import FormSection from 'components/Layout/FormSection';
import MultiCardDisplay from './stepComponents/MultiCardDisplay';
import { CardConfigurerStepProps } from '../CardConfigurerForm';
import {
  //
  RECIPIENT_METHOD_BULK,
  RECIPIENT_METHOD_MANUAL,
  RECIPIENT_TYPE_SELF,
  RECIPIENT_TYPE_SOMEONE_ELSE,
  BULK_RECIPIENTS_TEMPLATE,
} from 'utilities/constants';
import DownloadTemplateLink from './stepComponents/DownloadTemplateLink';
import BulkCsvUploadButton, { handleFileLoaded } from './stepComponents/BulkCsvUpload';
import HelperIcon from 'components/Helpers/HelperIcon';
import useNotification from 'effects/useNotification';
import { isCardDotGiftCard } from '../configurerFlags';
import FormLabel from '@mui/material/FormLabel';
import PanelLayout from './stepComponents/PanelLayout';

const d = debug('cardRecipient');

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      '&[hidden]': {
        display: 'none',
      },
    },
    field: {
      maxWidth: 400,
      '& .MuiFormHelperText-root': {
        color: '#585858',
      },
      '& .MuiInputLabel-root.Mui-error.Mui-required': {
        color: theme.palette.text.primary,
      },
      '& .MuiFormLabel-asterisk.Mui-error': {
        color: theme.palette.text.primary,
      },
      '& .MuiFormHelperText-root.Mui-error': {
        color: theme.palette.error.main,
      },
    },
    form: { flex: 1 },
    card: {
      flex: 1,
      textAlign: 'center',

      [theme.breakpoints.up('md')]: {
        textAlign: 'right',
      },
    },
    recipientMethod: {
      textAlign: 'center',

      [theme.breakpoints.up('md')]: {
        textAlign: 'left',
      },
    },
    infoIcon: {
      fontSize: '16px',
      '&:hover': {
        color: '#FFFB00',
      },
    },
    requirementContent: {
      '& .MuiTypography-root': {
        color: '#8F8F8F',
        marginLeft: theme.spacing(1),
      },
    },
    optionalFieldLabel: {
      '& .MuiInputLabel-root.Mui-error': {
        color: theme.palette.text.primary,
      },
    },
    standaloneLabel: {
      ...theme.typography.h3,
      color: 'white',
      marginBottom: theme.spacing(2),
    },
  }),
  { name: 'CardRecipient' },
);

// @ts-ignore
const Fields = ({ names, subscription, fieldsState = {}, children, originalRender }) => {
  if (!names.length) {
    return (originalRender || children)(fieldsState);
  }
  const [name, ...rest] = names;
  return (
    <Field name={name} subscription={subscription}>
      {(fieldState) => (
        // @ts-ignore
        <Fields names={rest} subscription={subscription} originalRender={originalRender || children} fieldsState={{ ...fieldsState, [name]: fieldState }} />
      )}
    </Field>
  );
};

const CardRecipient: React.FC<CardConfigurerStepProps> = ({ step, index, product, values, form, submitButton }) => {
  const classes = useStyles();
  const showMessage = useNotification();

  const onFileLoaded = handleFileLoaded(form, values, showMessage);

  const title = values.recipientCount === 'one' ? 'Who is this gift for?' : 'Add Recipients';

  const cardImageContent = <MultiCardDisplay defaultProductHandle={product.handle} values={values} showLogo={!!values.cardCustomImage && !isCardDotGiftCard(product)} />;

  const formContentForOneRecipient = (
    <Stack spacing={4} sx={{ alignItems: { xs: 'center', md: 'flex-start' } }}>
      <FormSection>
        <Field name="recipientType" defaultValue={RECIPIENT_TYPE_SOMEONE_ELSE}>
          {({ input: { value, onChange } }) => (
            <ToggleButtonGroup
              fullWidth={false}
              name="recipientType"
              items={[
                {
                  label: 'Someone Else',
                  value: RECIPIENT_TYPE_SOMEONE_ELSE,
                  helperText: 'Send a single card to someone else',
                },
                {
                  label: 'Myself',
                  value: RECIPIENT_TYPE_SELF,
                  helperText: 'Send one or more cards to a single address when you checkout',
                },
              ]}
              value={value}
              onChange={(event, newValue) => {
                newValue ? onChange(newValue) : onChange(value);
              }}
              rootSx={{ alignItems: { xs: 'center', md: 'flex-start' } }}
              helperSx={{ textAlign: { md: 'left' } }}
            />
          )}
        </Field>
        {values.recipientType === RECIPIENT_TYPE_SOMEONE_ELSE && (
          <Fragment>
            <FormLabel component="div" className={classes.standaloneLabel}>
              You can send via email as well as SMS
            </FormLabel>
            <TextField
              className={classes.field}
              variant="standard"
              label="Recipient Name"
              required
              name="recipients[0].name"
              fieldProps={{
                validate: composeValidators(isRequired),
              }}
            />
            <TextField
              fieldProps={{
                validate: composeValidators(isRequired, isValidEmail),
              }}
              className={classes.field}
              variant="standard"
              name="recipients[0].email"
              label="Recipient Email"
              required
            />
            <TextField
              className={`${classes.field} ${classes.optionalFieldLabel}`}
              variant="standard"
              label="Recipient Phone Number"
              name="recipients[0].phone"
              helperText="Send your gift card as an SMS (Optional)"
              inputProps={{ maxLength: 10 }}
              fieldProps={{
                validate: composeValidators(isValidPhoneNumber),
              }}
            />
          </Fragment>
        )}
      </FormSection>
    </Stack>
  );

  const formContentForMultipleRecipients = (
    <Stack direction={{ xs: 'column', md: 'row-reverse' }} spacing={{ xs: 2, md: 4 }} justifyContent={{ md: 'space-between' }}>
      <Box className={classes.form}>
        <Box className={classes.recipientMethod}>
          <Field name="recipientMethod" defaultValue={RECIPIENT_METHOD_MANUAL}>
            {({ input: { value, onChange } }) => (
              <ToggleButtonGroup
                name="recipientMethod"
                fullWidth={false}
                items={[
                  {
                    label: 'Add Manually',
                    value: RECIPIENT_METHOD_MANUAL,
                  },
                  {
                    label: 'Import Contacts',
                    value: RECIPIENT_METHOD_BULK,
                  },
                ]}
                value={value}
                onChange={(event, newValue) => {
                  newValue ? onChange(newValue) : onChange(value);
                }}
                rootSx={{ alignItems: { xs: 'center', md: 'flex-start' } }}
              />
            )}
          </Field>
        </Box>

        {values.recipientMethod === RECIPIENT_METHOD_MANUAL && (
          <RecipientForm
            onAddRecipient={(recipient: any) => {
              form.mutators.insert('recipients', 0, recipient);
            }}
          />
        )}
        {values.recipientMethod === RECIPIENT_METHOD_BULK && (
          <>
            <Typography variant="h3" gutterBottom marginTop={2}>
              Upload contact spreadsheet
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Use our spreadsheet template to upload multiple contacts at once
            </Typography>
            <Stack direction="row" spacing={3} marginTop={2} alignItems="center">
              <DownloadTemplateLink filename="bulk_template.csv" title="Download Template" content={BULK_RECIPIENTS_TEMPLATE} />
              <BulkCsvUploadButton onFileLoaded={onFileLoaded} label="Upload" />
            </Stack>
            <Stack direction={'row'} alignItems={'center'} spacing={3} marginTop={2} className={classes.requirementContent}>
              <HelperIcon fontSize="inherit" message="Spreadsheet requirements content TBC!" />
              <Typography variant="subtitle1">Spreadsheet Requirements</Typography>
            </Stack>
          </>
        )}

        <Box pt={4} pb={2} borderBottom={1} justifyContent="space-between" display="flex">
          <Typography variant="h3">View recipients</Typography>
          <Typography variant="h3">Total added: {(values.recipients && values.recipients.length) || 0}</Typography>
        </Box>
        <FieldArray
          name="recipients"
          validate={(recipients: any) => {
            if (!recipients || !recipients.length) {
              d('no RECIPIENTS');
              return 'You must add at least one recipient...';
            }
          }}
        >
          {({ meta, fields }) => (
            <>
              {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
              <AccordionWrapper
                // @ts-ignore
                items={fields}
              >
                {(Item: any, index: number, expanded: boolean, onChange: (event: React.SyntheticEvent<Element, Event>, isExpanded: boolean) => void, collapse: () => void) => (
                  <Accordion key={`recipient${index}`} expanded={expanded} onChange={onChange} disableGutters square>
                    <AccordionSummary color="primary" expandIcon={<ExpandMoreIcon />} aria-label="Expand" aria-controls={`panel${index}-recipeient-content`} id={`panel${index}-recipient-header`}>
                      <Typography>{get(values, `${Item}.name`, '')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gridGap: 10 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Card Value
                        </Typography>
                        <Typography variant="subtitle1">${values.cardValue}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="subtitle1">{get(values, `${Item}.email`, '')}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                          Card Type
                        </Typography>
                        <Typography variant="subtitle1">{values.cardMedium}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                          Written Message
                        </Typography>
                        <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                          {get(values, `${Item}.message`, '')}
                        </Typography>
                      </Box>
                      <br />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          fields.remove(index);
                          collapse();
                        }}
                        startIcon={<DeleteIcon />}
                      >
                        Remove
                      </Button>
                    </AccordionDetails>
                  </Accordion>
                )}
              </AccordionWrapper>
            </>
          )}
        </FieldArray>
      </Box>
    </Stack>
  );

  const formContent = values.recipientCount === 'one' ? formContentForOneRecipient : formContentForMultipleRecipients;

  return (
    <div className={classes.root} hidden={step !== index}>
      <PanelLayout value={step} index={index} title={title} cardImageContent={cardImageContent} formContent={formContent} buttonsContent={submitButton} />
    </div>
  );
};

export default CardRecipient;
